import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import holidayApiService, { HolidayDto } from "../../services/holiday-api";
import officeLocationsApiService, {
  OfficeLocation,
} from "../../services/office-locations-api";

interface HolidaysCardProps {
  onSave?: () => void;
  hideHeader?: boolean;
  shouldLoad?: boolean;
}

export interface HolidaysCardRef {
  openAddDialog: () => void;
  openImportDialog: () => void;
}

interface BulkImportData {
  date: string;
  type: string;
  description: string;
  locations: string;
  color?: string;
}

const HolidaysCard = forwardRef<HolidaysCardRef, HolidaysCardProps>(
  ({ onSave, hideHeader = false, shouldLoad = false }, ref) => {
    const { t } = useTranslation();
    const [holidays, setHolidays] = useState<HolidayDto[]>([]);
    const [officeLocations, setOfficeLocations] = useState<OfficeLocation[]>(
      [],
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [filterYear, setFilterYear] = useState<string>(
      new Date().getFullYear().toString(),
    );
    const [filterLocation, setFilterLocation] = useState<string>("");
    const [tabValue, setTabValue] = useState(0);
    const [bulkImportText, setBulkImportText] = useState("");
    const [bulkImportFormat, setBulkImportFormat] = useState<"csv" | "json">(
      "csv",
    );
    const [formData, setFormData] = useState<HolidayDto>({
      holidayDate: "",
      holidayTypeId: 1,
      description: "",
      reason: "",
      officeLocations: [],
    });

    useEffect(() => {
      if (shouldLoad) {
        loadHolidays();
        loadOfficeLocations();
      }
    }, [shouldLoad]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      openAddDialog: async () => {
        // Ensure office locations are loaded before opening dialog
        if (officeLocations.length === 0) {
          await loadOfficeLocations();
        }
        handleOpenDialog();
      },
      openImportDialog: async () => {
        // Ensure holidays are loaded for context
        if (holidays.length === 0) {
          await loadHolidays();
        }
        setTabValue(1);
      },
    }));

    const loadHolidays = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await holidayApiService.getAllHolidays();
        setHolidays(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t("config.errorHolidayLoad"),
        );
      } finally {
        setLoading(false);
      }
    };

    const loadOfficeLocations = async () => {
      try {
        const locations =
          await officeLocationsApiService.getAllOfficeLocations();
        setOfficeLocations(locations);
      } catch (err) {
        console.error(t("config.errorLocationLoad"), err);
      }
    };

    // Filter holidays by year and office location
    const filteredHolidays = useMemo(() => {
      return holidays.filter((holiday) => {
        const holidayYear = holiday.holidayDate.split("-")[0];
        const yearMatch = !filterYear || holidayYear === filterYear;
        const locationMatch =
          !filterLocation || holiday.officeLocations.includes(filterLocation);
        return yearMatch && locationMatch;
      });
    }, [holidays, filterYear, filterLocation]);

    // Get unique years from holidays
    const availableYears = useMemo(() => {
      const years = new Set(holidays.map((h) => h.holidayDate.split("-")[0]));
      // Always include current year
      years.add(new Date().getFullYear().toString());
      return Array.from(years).sort().reverse();
    }, [holidays]);

    // Parse CSV format: date,type,description,locations,color
    const parseCSV = (text: string): BulkImportData[] => {
      const lines = text.trim().split("\n");
      return lines
        .filter((line) => line.trim())
        .map((line) => {
          const [date, type, description, locations, color] = line
            .split(",")
            .map((s) => s.trim());
          return { date, type, description, locations, color };
        });
    };

    // Parse JSON format
    const parseJSON = (text: string): BulkImportData[] => {
      try {
        const data = JSON.parse(text);
        return Array.isArray(data) ? data : [data];
      } catch {
        throw new Error(t("config.errorInvalidJson"));
      }
    };

    const handleBulkImport = async () => {
      try {
        setLoading(true);
        setError(null);

        let importData: BulkImportData[] = [];
        if (bulkImportFormat === "csv") {
          importData = parseCSV(bulkImportText);
        } else {
          importData = parseJSON(bulkImportText);
        }

        if (importData.length === 0) {
          setError("No valid data found in import");
          return;
        }

        // Map type names to IDs
        const typeMap: { [key: string]: number } = {
          [t("config.holidayTypeRegular")]: 1,
          [t("config.holidayTypeCompany")]: 3,
        };

        // Prepare all holiday DTOs for bulk creation
        const holidayDtos: HolidayDto[] = importData.map((item) => {
          const typeId = typeMap[item.type] || 1;
          const locations = item.locations
            .split("|")
            .map((l) => l.trim())
            .filter((l) => l);

          return {
            holidayDate: item.date,
            holidayTypeId: typeId,
            description: item.description,
            reason: item.description,
            color: item.color || "#FF9800", // Default to orange if not provided
            officeLocations: locations,
          };
        });

        // Use bulk API to create all holidays with a single consolidated notification
        const createdHolidays =
          await holidayApiService.createHolidaysBulk(holidayDtos);

        setSuccess(`Imported ${createdHolidays.length} holidays`);
        setBulkImportText("");
        await loadHolidays();
        setTabValue(0);
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to import holidays",
        );
      } finally {
        setLoading(false);
      }
    };

    const handleOpenDialog = (holiday?: HolidayDto) => {
      if (holiday) {
        setEditingId(holiday.id || null);
        setFormData(holiday);
      } else {
        setEditingId(null);
        setFormData({
          holidayDate: "",
          holidayTypeId: 1,
          description: "",
          reason: "",
          officeLocations: [],
        });
      }
      setOpenDialog(true);
    };

    const handleCloseDialog = () => {
      setOpenDialog(false);
      setEditingId(null);
      setFormData({
        holidayDate: "",
        holidayTypeId: 1,
        description: "",
        reason: "",
        officeLocations: [],
      });
    };

    const colorOptions = [
      { value: "#E74C3C", label: "Red" },
      { value: "#27AE60", label: "Green" },
      { value: "#F1C40F", label: "Yellow" },
      { value: "#5DADE2", label: "Blue" },
      { value: "#9B59B6", label: "Purple" },
      { value: "#F1948A", label: "Pink" },
      { value: "#1ABC9C", label: "Teal" },
      { value: "#F5CBA7", label: "Peach" },
    ];

    const handleSaveHoliday = async () => {
      if (!formData.holidayDate || formData.officeLocations.length === 0) {
        setError(t("config.holidayRequired"));
        return;
      }

      try {
        setLoading(true);
        setError(null);

        if (editingId) {
          await holidayApiService.updateHoliday(editingId, formData);
          setSuccess(t("config.holidayUpdated"));
        } else {
          await holidayApiService.createHoliday(formData);
          setSuccess(t("config.holidayCreated"));
        }

        await loadHolidays();
        handleCloseDialog();
        onSave?.();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(err instanceof Error ? err.message : t("config.errorHoliday"));
      } finally {
        setLoading(false);
      }
    };

    const handleDeleteHoliday = async (holidayId: number | undefined) => {
      if (!holidayId) return;

      if (window.confirm(t("config.deleteHolidayConfirm"))) {
        try {
          setLoading(true);
          await holidayApiService.deleteHoliday(holidayId);
          setSuccess(t("config.holidayDeleted"));
          await loadHolidays();
          setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
          setError(
            err instanceof Error ? err.message : t("config.errorHolidayDelete"),
          );
        } finally {
          setLoading(false);
        }
      }
    };

    return (
      <Card
        sx={{
          mb: hideHeader ? 0 : 3,
          borderRadius: 2,
          boxShadow: hideHeader ? "none" : "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <CardContent sx={{ p: hideHeader ? 0 : 3 }}>
          {!hideHeader && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                🗓️ {t("config.holidays")}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Bulk import holidays">
                  <Button
                    startIcon={<UploadIcon />}
                    onClick={() => setTabValue(1)}
                    variant="outlined"
                    size="small"
                  >
                    {t("common.import")}
                  </Button>
                </Tooltip>
                <Tooltip title={t("config.addNewHoliday")}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    variant="contained"
                    size="small"
                  >
                    {t("common.add")}
                  </Button>
                </Tooltip>
              </Stack>
            </Box>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}

          {/* Tabs for View and Import */}
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ mb: 2 }}
          >
            <Tab label={t("common.view")} />
            <Tab label={t("common.import")} />
          </Tabs>

          {/* View Tab */}
          {tabValue === 0 && (
            <>
              {/* Filters */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mb: 3 }}
              >
                <FormControl sx={{ minWidth: 150 }} size="small">
                  <InputLabel>{t("common.year")}</InputLabel>
                  <Select
                    value={filterYear}
                    onChange={(e) => setFilterYear(e.target.value)}
                    label={t("common.year")}
                  >
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 150 }} size="small">
                  <InputLabel>{t("config.locations")}</InputLabel>
                  <Select
                    value={filterLocation}
                    onChange={(e) => setFilterLocation(e.target.value)}
                    label={t("config.locations")}
                  >
                    <MenuItem value="">All Locations</MenuItem>
                    {officeLocations.map((loc) => (
                      <MenuItem key={loc.id} value={loc.name}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              {loading && !holidays.length ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "action.hover" }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t("config.date")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t("config.type")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t("config.description")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t("config.locations")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t("config.color")}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, width: 100 }}
                        >
                          {t("common.actions")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredHolidays.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                            <Typography color="text.secondary">
                              {t("config.noHolidays")}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredHolidays.map((holiday) => (
                          <TableRow key={holiday.id} hover>
                            <TableCell>{holiday.holidayDate}</TableCell>
                            <TableCell>{holiday.holidayTypeName}</TableCell>
                            <TableCell>{holiday.description || "-"}</TableCell>
                            <TableCell>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                flexWrap="wrap"
                              >
                                {holiday.officeLocations.map((loc) => (
                                  <Chip key={loc} label={loc} size="small" />
                                ))}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: 1,
                                  backgroundColor: holiday.color || "#FF9800",
                                  border: 1,
                                  borderColor: "divider",
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={t("config.tooltipEdit")}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDialog(holiday)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t("config.tooltipDelete")}>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleDeleteHoliday(holiday.id)
                                  }
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Import Tab */}
          {tabValue === 1 && (
            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Format</InputLabel>
                <Select
                  value={bulkImportFormat}
                  onChange={(e) =>
                    setBulkImportFormat(e.target.value as "csv" | "json")
                  }
                  label={t("config.format")}
                >
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="json">JSON</MenuItem>
                </Select>
              </FormControl>

              <Box
                sx={{
                  bgcolor: "background.paper",
                  p: 2,
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  {bulkImportFormat === "csv"
                    ? "CSV Format: date,type,description,locations,color (one per line)\nExample: 2026-01-01,Regular Holiday,New Year,Japan|Vietnam,#FF9800\nNote: color is optional, defaults to #FF9800 (orange)"
                    : 'JSON Format: Array of objects with date, type, description, locations, color\nExample: [{"date":"2026-01-01","type":"Regular Holiday","description":"New Year","locations":"Japan|Vietnam","color":"#FF9800"}]\nNote: color is optional, defaults to #FF9800 (orange)'}
                </Typography>
              </Box>

              <TextField
                label={t("config.pasteHolidaysHere")}
                multiline
                rows={8}
                value={bulkImportText}
                onChange={(e) => setBulkImportText(e.target.value)}
                placeholder={
                  bulkImportFormat === "csv"
                    ? "2026-01-01,Regular Holiday,New Year,Japan|Vietnam,#FF9800\n2026-02-11,Regular Holiday,Foundation Day,Japan,#FF5722"
                    : '[{"date":"2026-01-01","type":"Regular Holiday","description":"New Year","locations":"Japan|Vietnam","color":"#FF9800"}]'
                }
                fullWidth
                variant="outlined"
                size="small"
              />

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => {
                    setBulkImportText("");
                    setTabValue(0);
                  }}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleBulkImport}
                  disabled={loading || !bulkImportText.trim()}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    t("common.import")
                  )}
                </Button>
              </Stack>
            </Stack>
          )}
        </CardContent>

        {/* Add/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingId ? t("config.editHoliday") : t("config.addHoliday")}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2} sx={{ pt: 2 }}>
              <TextField
                label={t("config.date")}
                type="date"
                value={formData.holidayDate}
                onChange={(e) =>
                  setFormData({ ...formData, holidayDate: e.target.value })
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <FormControl fullWidth size="small">
                <InputLabel>{t("config.type")}</InputLabel>
                <Select
                  value={formData.holidayTypeId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      holidayTypeId: e.target.value as number,
                    })
                  }
                  label={t("config.type")}
                >
                  <MenuItem value={1}>Regular Holiday</MenuItem>
                  <MenuItem value={3}>Company Holiday</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={t("config.description")}
                value={formData.description || ""}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                fullWidth
                multiline
                rows={2}
                size="small"
              />
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t("config.color")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  sx={{ mb: 1.5 }}
                >
                  {colorOptions.map((option) => (
                    <Tooltip key={option.value} title={option.label}>
                      <Box
                        onClick={() =>
                          setFormData({ ...formData, color: option.value })
                        }
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          backgroundColor: option.value,
                          border: 2,
                          borderColor:
                            formData.color === option.value
                              ? "primary.main"
                              : "divider",
                          cursor: "pointer",
                          "&:hover": {
                            borderColor: "primary.main",
                          },
                        }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
                <TextField
                  label={t("config.customColor")}
                  value={formData.color || "#FF9800"}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setFormData({ ...formData, color: value });
                  }}
                  fullWidth
                  size="small"
                  placeholder="#XXXXXX"
                  helperText="Enter hex color code (e.g., #E74C3C, #27AE60)"
                  inputProps={{
                    maxLength: 7,
                    style: { fontFamily: "monospace" },
                  }}
                />
              </Box>
              <Autocomplete
                multiple
                options={officeLocations}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.name
                }
                value={
                  officeLocations.filter((loc) =>
                    formData.officeLocations.includes(loc.name),
                  ) || []
                }
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    officeLocations: newValue.map((v) =>
                      typeof v === "string" ? v : v.name,
                    ),
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("config.locations")}
                    size="small"
                  />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog} variant="outlined">
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSaveHoliday}
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t("common.save")}
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    );
  },
);

HolidaysCard.displayName = "HolidaysCard";

export default React.memo(HolidaysCard);
